

import React, { ReactNode } from 'react';
import { useLocation, Link } from 'react-router-dom';

interface PageTitleProps {
    title: string;
    subtitle?: string;
    children?: ReactNode;
}

export const PageTitle: React.FC<PageTitleProps> = ({ title, subtitle, children }) => (
    <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{title}</h1>
            {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex-shrink-0">
            {children}
        </div>
    </div>
);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'dark' | 'outline';
    size?: 'sm' | 'md' | 'lg' | 'xl';
    as?: React.ElementType;
    to?: string;
    href?: string;
    download?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ children, variant = 'primary', size = 'md', className, as: Component = 'button', ...props }) => {
    const baseClasses = "rounded-lg font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 whitespace-nowrap";
    const variantClasses = {
        primary: 'bg-gradient-to-r from-red-500 to-yellow-400 text-white shadow-md hover:from-red-600 hover:to-yellow-500 focus:ring-red-500',
        secondary: 'bg-gray-200 hover:bg-gray-300 focus:ring-gray-400 text-gray-800 shadow-sm',
        danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white shadow-sm',
        ghost: 'bg-transparent hover:bg-gray-100 focus:ring-primary text-primary',
        dark: 'bg-gray-800 hover:bg-gray-900 focus:ring-gray-700 text-white shadow-sm',
        outline: 'bg-transparent border border-gray-300 hover:bg-gray-50 text-gray-700 focus:ring-red-500',
    };
    const sizeClasses = {
        sm: 'px-3 py-1.5 text-xs',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
        xl: 'px-8 py-4 text-base'
    };
    
    const finalProps = { ...props };
    if (Component === 'button' && (finalProps as any).to) {
      delete (finalProps as any).to;
    }


    return (
        <Component className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`} {...finalProps}>
            {children}
        </Component>
    );
};

// Fix: Update Card component to accept and pass down additional HTML attributes like `draggable` and `onDragStart`.
export const Card: React.FC<{ children: ReactNode; className?: string; as?: React.ElementType } & React.HTMLAttributes<HTMLElement>> = ({ children, className = '', as: Component = 'div', ...props }) => (
    <Component className={`bg-white rounded-2xl shadow-xl ${className}`} {...props}>
        {children}
    </Component>
);

export const Breadcrumbs: React.FC<{allNavItems: { path: string, label: string }[]}> = ({ allNavItems }) => {
    const location = useLocation();
    const pathnames = location.pathname.split('/').filter(x => x);

    const getPageTitle = (path: string) => {
        if (path.startsWith('/courses/')) {
            return 'Course Details';
        }
        const item = allNavItems.find(item => path.startsWith(item.path));
        return item ? item.label : 'Dashboard';
    }

    return (
        <nav className="text-sm font-medium hidden md:block">
            <ol className="list-none p-0 inline-flex items-center text-gray-500">
                <li className="flex items-center">
                    <Link to="/dashboard" className="hover:text-primary">Dashboard</Link>
                </li>
                {pathnames.length > 0 && pathnames[0] !== 'dashboard' && (
                    <li className="flex items-center">
                        <span className="mx-2">/</span>
                        <span>{getPageTitle(location.pathname)}</span>
                    </li>
                )}
            </ol>
        </nav>
    );
};

export const EmptyState: React.FC<{ title: string; message: string; action?: ReactNode }> = ({ title, message, action }) => (
    <Card className="text-center py-12 px-6">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <p className="text-gray-500 mt-2 mb-4">{message}</p>
        {action}
    </Card>
);

export const LoadingSkeleton: React.FC<{ className?: string }> = ({ className }) => (
    <div className={`animate-pulse bg-gray-200 rounded-md ${className}`}></div>
);
